import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import LinkExtension from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import {
  ArrowLeft, Save, Eye, Bold, Italic, List, ListOrdered, Quote,
  Link2, Image, Youtube as YoutubeIcon, Heading1, Heading2, Heading3,
  Code, Undo, Redo, X, Plus, Upload, Cloud, CloudOff,
  LayoutGrid, AlignLeft, AlignCenter, AlignRight, Maximize
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { ResizableImage, AlignableYoutube, GalleryExtension } from '../../components/editor/extensions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PostEditor = () => {
  const navigate = useNavigate();
  const { postId } = useParams();
  const { token } = useAuth();
  const isEditing = !!postId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [driveStatus, setDriveStatus] = useState({ connected: false });
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    featured_image: '',
    gallery: [],
    video_url: '',
    category: '',
    tags: [],
    published: false,
  });

  const [newTag, setNewTag] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [loadedContent, setLoadedContent] = useState(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      ResizableImage.configure({ inline: false }),
      LinkExtension.configure({ openOnClick: false }),
      AlignableYoutube.configure({ controls: true }),
      GalleryExtension,
      Placeholder.configure({ placeholder: 'Start writing your story...' }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose prose-stone max-w-none focus:outline-none min-h-[400px] p-4',
      },
    },
  });

  // Set editor content once both editor and loaded content are ready
  useEffect(() => {
    if (editor && loadedContent !== null) {
      editor.commands.setContent(loadedContent);
      setLoadedContent(null);
    }
  }, [editor, loadedContent]);

  useEffect(() => {
    fetchCategories();
    fetchDriveStatus();
    if (isEditing) {
      fetchPost();
    }
  }, [postId]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchDriveStatus = async () => {
    try {
      const response = await axios.get(`${API}/dropbox/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDriveStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch Dropbox status:', error);
    }
  };

  const fetchPost = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const post = response.data;
      setFormData({
        title: post.title,
        excerpt: post.excerpt,
        featured_image: post.featured_image,
        gallery: post.gallery || [],
        video_url: post.video_url,
        category: post.category,
        tags: post.tags || [],
        published: post.published,
      });
      if (post.content) {
        if (editor) {
          editor.commands.setContent(post.content);
        } else {
          setLoadedContent(post.content);
        }
      }
    } catch (error) {
      toast.error('Failed to load post');
      navigate('/admin/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (publish = false) => {
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        content: editor?.getHTML() || '',
        published: publish || formData.published,
      };

      if (isEditing) {
        await axios.put(`${API}/posts/${postId}`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Post updated');
      } else {
        await axios.post(`${API}/posts`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Post created');
      }
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error('Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, newTag.trim()] });
      setNewTag('');
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter((t) => t !== tag) });
  };

  const handleCreateCategory = async () => {
    if (!newCategory.trim()) return;
    try {
      const response = await axios.post(
        `${API}/categories`,
        { name: newCategory.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCategories([...categories, response.data]);
      setFormData({ ...formData, category: newCategory.trim() });
      setNewCategory('');
      toast.success('Category created');
    } catch (error) {
      toast.error('Failed to create category');
    }
  };

  const handleFileUpload = async (e, type = 'featured') => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!driveStatus.connected) {
      toast.error('Please connect Dropbox first');
      return;
    }

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/dropbox/upload`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      const url = response.data.url;

      if (type === 'featured') {
        setFormData({ ...formData, featured_image: url });
      } else if (type === 'gallery') {
        setFormData({ ...formData, gallery: [...formData.gallery, url] });
      } else if (type === 'content') {
        editor?.chain().focus().setImage({ src: url }).run();
      }

      toast.success('Image uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [youtubeDialogOpen, setYoutubeDialogOpen] = useState(false);

  const addImageByUrl = () => {
    if (imageUrl.trim()) {
      editor?.chain().focus().setImage({ src: imageUrl }).run();
      setImageUrl('');
      setImageDialogOpen(false);
    }
  };

  const addYoutubeVideo = () => {
    if (youtubeUrl.trim()) {
      editor?.chain().focus().setYoutubeVideo({ src: youtubeUrl }).run();
      setYoutubeUrl('');
      setYoutubeDialogOpen(false);
    }
  };

  const setLink = useCallback(() => {
    const previousUrl = editor?.getAttributes('link').href;
    const url = window.prompt('Enter URL', previousUrl);

    if (url === null) return;
    if (url === '') {
      editor?.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-800"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100" data-testid="post-editor-page">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/admin/dashboard"
              className="text-stone-500 hover:text-stone-900 transition-colors"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft size={20} />
            </Link>
            <span className="font-serif text-lg text-stone-900">
              {isEditing ? 'Edit Story' : 'New Story'}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 mr-4">
              <Switch
                checked={formData.published}
                onCheckedChange={(checked) => setFormData({ ...formData, published: checked })}
                data-testid="publish-toggle"
              />
              <Label className="text-sm text-stone-600">
                {formData.published ? 'Published' : 'Draft'}
              </Label>
            </div>
            <Button
              variant="outline"
              onClick={() => handleSave(false)}
              disabled={saving}
              className="border-stone-300"
              data-testid="save-draft-btn"
            >
              <Save size={16} className="mr-2" />
              Save
            </Button>
            <Button
              onClick={() => handleSave(true)}
              disabled={saving}
              className="bg-stone-900 hover:bg-red-800"
              data-testid="publish-btn"
            >
              <Eye size={16} className="mr-2" />
              {saving ? 'Saving...' : 'Publish'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title */}
            <div>
              <Input
                type="text"
                placeholder="Story Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="text-3xl font-serif border-0 border-b border-stone-200 rounded-none px-0 py-4 focus:border-stone-400 focus:ring-0 bg-transparent"
                data-testid="post-title-input"
              />
            </div>

            {/* Excerpt */}
            <div>
              <Label className="text-stone-600 mb-2 block">Excerpt / Summary</Label>
              <Textarea
                placeholder="A brief summary of your story (used in previews and SEO)..."
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                rows={3}
                className="bg-white border-stone-200 rounded-sm resize-none"
                data-testid="post-excerpt-input"
              />
            </div>

            {/* Editor Toolbar */}
            <div className="bg-white border border-stone-200 rounded-sm">
              <div className="flex flex-wrap items-center gap-1 p-2 border-b border-stone-200">
                <button
                  onClick={() => editor?.chain().focus().toggleBold().run()}
                  className={`p-2 rounded hover:bg-stone-100 ${editor?.isActive('bold') ? 'bg-stone-100' : ''}`}
                  title="Bold"
                >
                  <Bold size={18} />
                </button>
                <button
                  onClick={() => editor?.chain().focus().toggleItalic().run()}
                  className={`p-2 rounded hover:bg-stone-100 ${editor?.isActive('italic') ? 'bg-stone-100' : ''}`}
                  title="Italic"
                >
                  <Italic size={18} />
                </button>
                <div className="w-px h-6 bg-stone-200 mx-1" />
                <button
                  onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
                  className={`p-2 rounded hover:bg-stone-100 ${editor?.isActive('heading', { level: 1 }) ? 'bg-stone-100' : ''}`}
                  title="Heading 1"
                >
                  <Heading1 size={18} />
                </button>
                <button
                  onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                  className={`p-2 rounded hover:bg-stone-100 ${editor?.isActive('heading', { level: 2 }) ? 'bg-stone-100' : ''}`}
                  title="Heading 2"
                >
                  <Heading2 size={18} />
                </button>
                <button
                  onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
                  className={`p-2 rounded hover:bg-stone-100 ${editor?.isActive('heading', { level: 3 }) ? 'bg-stone-100' : ''}`}
                  title="Heading 3"
                >
                  <Heading3 size={18} />
                </button>
                <div className="w-px h-6 bg-stone-200 mx-1" />
                <button
                  onClick={() => editor?.chain().focus().toggleBulletList().run()}
                  className={`p-2 rounded hover:bg-stone-100 ${editor?.isActive('bulletList') ? 'bg-stone-100' : ''}`}
                  title="Bullet List"
                >
                  <List size={18} />
                </button>
                <button
                  onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                  className={`p-2 rounded hover:bg-stone-100 ${editor?.isActive('orderedList') ? 'bg-stone-100' : ''}`}
                  title="Ordered List"
                >
                  <ListOrdered size={18} />
                </button>
                <button
                  onClick={() => editor?.chain().focus().toggleBlockquote().run()}
                  className={`p-2 rounded hover:bg-stone-100 ${editor?.isActive('blockquote') ? 'bg-stone-100' : ''}`}
                  title="Quote"
                >
                  <Quote size={18} />
                </button>
                <button
                  onClick={() => editor?.chain().focus().toggleCodeBlock().run()}
                  className={`p-2 rounded hover:bg-stone-100 ${editor?.isActive('codeBlock') ? 'bg-stone-100' : ''}`}
                  title="Code Block"
                >
                  <Code size={18} />
                </button>
                <div className="w-px h-6 bg-stone-200 mx-1" />
                <button
                  onClick={setLink}
                  className={`p-2 rounded hover:bg-stone-100 ${editor?.isActive('link') ? 'bg-stone-100' : ''}`}
                  title="Add Link"
                >
                  <Link2 size={18} />
                </button>
                
                {/* Image Dialog */}
                <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
                  <DialogTrigger asChild>
                    <button className="p-2 rounded hover:bg-stone-100" title="Add Image">
                      <Image size={18} />
                    </button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Image</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label className="mb-2 block">Image URL</Label>
                        <div className="flex gap-2">
                          <Input
                            value={imageUrl}
                            onChange={(e) => setImageUrl(e.target.value)}
                            placeholder="https://..."
                          />
                          <Button onClick={addImageByUrl}>Add</Button>
                        </div>
                      </div>
                      {driveStatus.connected && (
                        <div>
                          <Label className="mb-2 block">Or upload from device</Label>
                          <Input
                            type="file"
                            accept="image/*"
                            onChange={(e) => { handleFileUpload(e, 'content'); setImageDialogOpen(false); }}
                            disabled={uploading}
                          />
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>

                {/* YouTube Dialog */}
                <Dialog open={youtubeDialogOpen} onOpenChange={setYoutubeDialogOpen}>
                  <DialogTrigger asChild>
                    <button className="p-2 rounded hover:bg-stone-100" title="Add YouTube Video">
                      <YoutubeIcon size={18} />
                    </button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add YouTube Video</DialogTitle>
                    </DialogHeader>
                    <div>
                      <Label className="mb-2 block">YouTube URL</Label>
                      <div className="flex gap-2">
                        <Input
                          value={youtubeUrl}
                          onChange={(e) => setYoutubeUrl(e.target.value)}
                          placeholder="https://www.youtube.com/watch?v=..."
                        />
                        <Button onClick={addYoutubeVideo}>Add</Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Gallery Button */}
                <button
                  onClick={() => {
                    editor?.chain().focus().insertContent({
                      type: 'gallery',
                      attrs: { images: [], columns: 3, gap: 8 }
                    }).run();
                  }}
                  className="p-2 rounded hover:bg-stone-100"
                  title="Insert Gallery"
                  data-testid="insert-gallery-btn"
                >
                  <LayoutGrid size={18} />
                </button>

                <div className="w-px h-6 bg-stone-200 mx-1" />
                <button
                  onClick={() => editor?.chain().focus().undo().run()}
                  className="p-2 rounded hover:bg-stone-100"
                  title="Undo"
                >
                  <Undo size={18} />
                </button>
                <button
                  onClick={() => editor?.chain().focus().redo().run()}
                  className="p-2 rounded hover:bg-stone-100"
                  title="Redo"
                >
                  <Redo size={18} />
                </button>
              </div>

              {/* Editor Content */}
              <EditorContent editor={editor} data-testid="post-content-editor" />
              <div className="px-4 py-2 border-t border-stone-100 text-xs text-stone-400">
                Click any image or video to resize, align (left/center/right/full), crop, or rotate. Use the gallery button to create photo grids.
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Featured Image */}
            <div className="bg-white p-6 rounded-sm border border-stone-200">
              <Label className="text-stone-900 font-medium mb-4 block">Featured Image</Label>
              {formData.featured_image ? (
                <div className="relative">
                  <img
                    src={formData.featured_image}
                    alt="Featured"
                    className="w-full rounded-sm mb-4"
                  />
                  <button
                    onClick={() => setFormData({ ...formData, featured_image: '' })}
                    className="absolute top-2 right-2 p-1 bg-white rounded-full shadow"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-stone-200 rounded-sm p-8 text-center mb-4">
                  <Image size={32} className="text-stone-300 mx-auto mb-2" />
                  <p className="text-sm text-stone-500">No image selected</p>
                </div>
              )}
              <Input
                type="text"
                placeholder="Image URL"
                value={formData.featured_image}
                onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                className="mb-2"
                data-testid="featured-image-url-input"
              />
              {driveStatus.connected && (
                <div className="relative">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, 'featured')}
                    disabled={uploading}
                    className="hidden"
                    id="featured-upload"
                  />
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => document.getElementById('featured-upload')?.click()}
                    disabled={uploading}
                    data-testid="upload-featured-image-btn"
                  >
                    <Upload size={16} className="mr-2" />
                    {uploading ? 'Uploading...' : 'Upload Image'}
                  </Button>
                </div>
              )}
              {!driveStatus.connected && (
                <p className="text-xs text-stone-500 mt-2 flex items-center gap-1">
                  <CloudOff size={12} /> Connect Dropbox to upload images
                </p>
              )}
            </div>

            {/* Category */}
            <div className="bg-white p-6 rounded-sm border border-stone-200">
              <Label className="text-stone-900 font-medium mb-4 block">Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger className="w-full" data-testid="category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.name}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-2 mt-4">
                <Input
                  placeholder="New category"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  data-testid="new-category-input"
                />
                <Button variant="outline" size="icon" onClick={handleCreateCategory}>
                  <Plus size={16} />
                </Button>
              </div>
            </div>

            {/* Tags */}
            <div className="bg-white p-6 rounded-sm border border-stone-200">
              <Label className="text-stone-900 font-medium mb-4 block">Tags</Label>
              <div className="flex flex-wrap gap-2 mb-4">
                {formData.tags.map((tag) => (
                  <Badge
                    key={tag}
                    variant="secondary"
                    className="bg-stone-100 text-stone-700 cursor-pointer hover:bg-red-100 hover:text-red-800"
                    onClick={() => handleRemoveTag(tag)}
                  >
                    {tag} <X size={12} className="ml-1" />
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add tag"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  data-testid="new-tag-input"
                />
                <Button variant="outline" size="icon" onClick={handleAddTag}>
                  <Plus size={16} />
                </Button>
              </div>
            </div>

            {/* Video URL */}
            <div className="bg-white p-6 rounded-sm border border-stone-200">
              <Label className="text-stone-900 font-medium mb-4 block">Video URL (YouTube/Vimeo)</Label>
              <Input
                type="text"
                placeholder="https://youtube.com/watch?v=..."
                value={formData.video_url}
                onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                data-testid="video-url-input"
              />
            </div>

            {/* Gallery */}
            <div className="bg-white p-6 rounded-sm border border-stone-200">
              <Label className="text-stone-900 font-medium mb-4 block">Gallery Images</Label>
              <div className="grid grid-cols-3 gap-2 mb-4">
                {formData.gallery.map((img, index) => (
                  <div key={index} className="relative aspect-square">
                    <img src={img} alt="" className="w-full h-full object-cover rounded-sm" />
                    <button
                      onClick={() =>
                        setFormData({
                          ...formData,
                          gallery: formData.gallery.filter((_, i) => i !== index),
                        })
                      }
                      className="absolute top-1 right-1 p-0.5 bg-white rounded-full shadow"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
              {driveStatus.connected && (
                <div className="relative">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, 'gallery')}
                    disabled={uploading}
                    className="hidden"
                    id="gallery-upload"
                  />
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => document.getElementById('gallery-upload')?.click()}
                    disabled={uploading}
                    data-testid="upload-gallery-image-btn"
                  >
                    <Plus size={16} className="mr-2" />
                    Add to Gallery
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PostEditor;
